/**
 * Datos del tablero (Req. 17).
 *
 * NO tiene logica de negocio propia: solo junta en una llamada lo que las demas
 * areas ya calculan. Cualquier regla nueva aqui acabaria contradiciendo a la
 * pantalla original, que es el problema que se evito en la Fase 4 al compartir
 * `necesidadesPorInsumo` entre produccion y lista de compras.
 *
 * Todo lo que hace es LEER. Ninguna funcion escribe, para que se pueda usar
 * dentro de `useLiveQuery` sin provocar el error de transaccion de escritura en
 * contexto de lectura que rompio la aplicacion en produccion antes.
 */

import { db } from '../schema';
import { hoy, sumarDias, type FechaLocal } from '@/domain/dates';
import { formatearCentavos, type Centavos } from '@/domain/money';
import { verAgenda, type TareaProduccion } from './production';
import { resumenDeFaltantes, type ResumenFaltantes } from './shopping';
import { verResumen } from './reports';
import { pendientesDeVenta, type PendienteDeVenta } from './sales';
import { resumenInventario, type ResumenInventario } from './inventory';
import { platillosDesde } from './menu';
import { pedidosDeFecha, pedidosPorRecetaEntre } from './orders';
import type { PlannedItem } from '../types';

export interface ResumenDelDia {
  fecha: FechaLocal;
  /** Se vende hoy. */
  entregas: TareaProduccion[];
  /** Hay que cocinarlo hoy. */
  preparar: TareaProduccion[];
  /** Se debio cocinar antes y no se hizo. */
  atrasados: TareaProduccion[];
  /**
   * Lo que se va a cobrar hoy segun los PEDIDOS, con el precio de cada quien.
   *
   * Antes se calculaba como porciones estimadas del menu por precio de lista, y
   * daba un numero que no correspondia a nada: decia $600 cuando lo pedido sumaba
   * $580, porque a un cliente frecuente se le cobra $100 en lugar de $120. Un
   * numero que no cuadra con ninguna otra pantalla estorba mas de lo que ayuda.
   */
  ventaEsperadaCentavos: Centavos;
  /** Porciones pedidas para hoy, de las que sale esa venta esperada. */
  porcionesPedidasHoy: number;
  /** Platillos de hoy que aun no tienen su venta registrada. */
  ventasPendientes: PendienteDeVenta[];
  /** Ingreso ya registrado hoy. */
  ingresoDeHoyCentavos: Centavos;
  /** Lo que falta comprar en los proximos dias. */
  faltantes: ResumenFaltantes;
  /** Resultado de la semana en curso. */
  semana: {
    ingresoCentavos: Centavos;
    resultadoNetoCentavos: Centavos;
    costoIncompleto: boolean;
    cuantasVentas: number;
  };
  inventario: ResumenInventario;
  /** true si no hay ningun platillo en el menu de hoy en adelante. */
  sinMenu: boolean;
  /** Cuantos platillos hay planeados de hoy en adelante. */
  platillosPorVenir: number;
}

/**
 * Todo lo que el tablero necesita, en una sola lectura.
 *
 * Se resuelve en secuencia y no en paralelo a proposito: Dexie comparte la
 * transaccion de `useLiveQuery`, y lanzar varias consultas simultaneas dentro de
 * ella complica el seguimiento de dependencias que hace la suscripcion.
 */
export async function verResumenDelDia(fecha: FechaLocal = hoy()): Promise<ResumenDelDia> {
  const agenda = await verAgenda(fecha);
  const faltantes = await resumenDeFaltantes('proximos', fecha);
  const semana = await verResumen('semana', fecha);
  const inventario = await resumenInventario();
  const pendientes = await pendientesDeVenta(fecha);
  const porVenir = await platillosDesde(fecha);

  /**
   * Los pedidos que se entregan hoy, con el precio que se le cobra a cada quien.
   * De aqui sale la venta esperada, y no del estimado del menu.
   */
  const pedidosDeHoy = (await pedidosDeFecha(fecha)).filter((o) => o.estado === 'activo');

  const ventasDeHoy = (await db.sales.where('fecha').equals(fecha).toArray());
  const anuladas = new Set(
    (await db.sales.toArray()).map((v) => v.corrigeA).filter((x) => x !== null),
  );

  return {
    fecha,
    entregas: agenda.entregasDeHoy,
    preparar: agenda.prepararHoy,
    atrasados: agenda.atrasados,
    ventaEsperadaCentavos: pedidosDeHoy.reduce((s, o) => s + o.totalCentavos, 0),
    porcionesPedidasHoy: pedidosDeHoy.reduce((s, o) => s + o.porciones, 0),
    ventasPendientes: pendientes.filter((p) => !p.cerrado),
    ingresoDeHoyCentavos: ventasDeHoy
      .filter((v) => !anuladas.has(v.id))
      .reduce((s, v) => s + v.ingresoCentavos, 0),
    faltantes,
    semana: {
      ingresoCentavos: semana.ingresoCentavos,
      resultadoNetoCentavos: semana.resultadoNetoCentavos,
      costoIncompleto: semana.costoIncompleto,
      cuantasVentas: semana.cuantasVentas,
    },
    inventario,
    sinMenu: porVenir.length === 0,
    platillosPorVenir: porVenir.length,
  };
}

/**
 * Frase corta que resume el dia, para encabezar el tablero.
 *
 * Existe para que la primera linea de la pantalla ya responda "que tengo que
 * hacer", sin que haya que leer tres secciones para deducirlo.
 */
export function fraseDelDia(resumen: ResumenDelDia): string {
  if (resumen.atrasados.length > 0) {
    return `Tienes ${resumen.atrasados.length} ${
      resumen.atrasados.length === 1 ? 'platillo atrasado' : 'platillos atrasados'
    }`;
  }
  if (resumen.preparar.length > 0) {
    return `Hoy toca cocinar ${resumen.preparar.length} ${
      resumen.preparar.length === 1 ? 'platillo' : 'platillos'
    }`;
  }
  if (resumen.ventasPendientes.length > 0) {
    return `Registra la venta de ${resumen.ventasPendientes.length} ${
      resumen.ventasPendientes.length === 1 ? 'platillo' : 'platillos'
    }`;
  }
  if (resumen.entregas.length > 0) {
    return `Hoy entregas ${formatearCentavos(resumen.ventaEsperadaCentavos)}`;
  }
  if (resumen.sinMenu) return 'Todavía no hay menú planeado';
  return 'Nada pendiente por hoy';
}

/** Los proximos dias con platillos, para ver que viene sin salir del tablero. */
export interface DiaPorVenir {
  fecha: FechaLocal;
  /** Platillos que se venden ese dia. */
  entregas: PlatilloPorVenir[];
  /** Platillos que hay que cocinar ese dia. */
  preparaciones: Array<PlatilloPorVenir & { paraCuando: FechaLocal }>;
}

/**
 * Un platillo de los proximos dias, con lo pedido y lo estimado por separado.
 *
 * Se devuelven los dos numeros y no uno solo porque significan cosas distintas:
 * `porcionesPedidas` es gente esperando su comida y `porcionesEstimadas` es lo
 * que se apunto al planear el menu. Quien muestra el dato decide cual manda, pero
 * el que manda es el pedido.
 */
export interface PlatilloPorVenir {
  nombre: string;
  porcionesPedidas: number;
  porcionesEstimadas: number;
  /** Cuantas personas lo pidieron. En cero nadie ha pedido todavia. */
  cuantosPidieron: number;
}

/**
 * Que viene en los proximos dias.
 *
 * Se limita a unos pocos dias porque el tablero responde "hoy y lo inmediato";
 * la vista completa de la semana ya existe en la pantalla de menu.
 */
export async function verProximosDias(
  desde: FechaLocal = hoy(),
  cuantos = 3,
): Promise<DiaPorVenir[]> {
  const hasta = sumarDias(desde, cuantos);

  const platillos = (await db.plannedItems.toArray()).filter(
    (p) => p.fechaVenta >= desde || p.fechaPrep >= desde,
  );
  if (platillos.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(platillos.map((p) => p.recipeId))]))
      .filter((r) => r !== undefined)
      .map((r) => [r.id, r.nombre]),
  );

  /**
   * Lo que de verdad esta pedido de cada platillo.
   *
   * El rango arranca en `desde` y no en el dia siguiente porque un platillo que se
   * PREPARA dentro de la ventana puede venderse despues de `hasta`, y su pedido
   * hay que encontrarlo igual. Se toma el rango completo de las fechas de venta
   * involucradas.
   */
  const ventasEnJuego = platillos.map((p) => p.fechaVenta).sort((a, b) => a.localeCompare(b));
  const pedidos = await pedidosPorRecetaEntre(
    ventasEnJuego[0] ?? desde,
    ventasEnJuego[ventasEnJuego.length - 1] ?? hasta,
  );

  /** Manda lo pedido; el estimado viaja al lado para poder contrastarlo. */
  const describir = (p: PlannedItem): PlatilloPorVenir => {
    const pedido = pedidos.get(`${p.fechaVenta}|${p.recipeId}`);
    return {
      nombre: recetas.get(p.recipeId) ?? '(receta eliminada)',
      porcionesPedidas: pedido?.porciones ?? 0,
      porcionesEstimadas: p.porcionesPlaneadas,
      cuantosPidieron: pedido?.cuantos ?? 0,
    };
  };

  const dias: DiaPorVenir[] = [];

  for (let cursor = sumarDias(desde, 1); cursor <= hasta; cursor = sumarDias(cursor, 1)) {
    const entregas = platillos.filter((p) => p.fechaVenta === cursor).map(describir);

    const preparaciones = platillos
      .filter((p) => p.fechaPrep === cursor)
      .map((p) => ({ ...describir(p), paraCuando: p.fechaVenta }));

    if (entregas.length > 0 || preparaciones.length > 0) {
      dias.push({ fecha: cursor, entregas, preparaciones });
    }
  }

  return dias;
}
